import ChartDataLabels from 'chartjs-plugin-datalabels'

const ageRgbNumber = '106,159,58'
const channelColors = ['#4472C4', '#ED7D31', '#FFC000', '#F44F39', '#E27C7F']
const channelShareColors = [
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4',
  '#FABFBD', '#F8CBAD', '#FFE699', '#C5E0B4', '#BDD7EE', '#DCD4F4'
]

const defProps = ({ usePercentage, labelAlignCenter, scales, height = 160, legend, extras, tooltips = {} }) => ({
  options: {
    plugins: {
      datalabels: {
        anchor: labelAlignCenter ? '' : 'end',
        align: labelAlignCenter ? '' : 'end',
        formatter: value => usePercentage ? `${value}%` : value?.toLocaleString()
      }
    },
    legend: legend || { display: false },
    interaction: { mode: 'index', intersect: true },
    tooltips: {
      mode: 'x-axis',
      intersect: true,
      displayColors: false,
      callbacks: { label: i => usePercentage ? `${i?.value}%` : i?.value?.toLocaleString() },
      ...tooltips
    },
    scales
  },
  plugins: [ChartDataLabels],
  height,
  ...extras
})

export const genderChartProps = ({ data, ln_t }) => ({
  data: {
    datasets: [{
      data: ['female', 'male', 'other'].map(i => data?.[i]?.percentage),
      backgroundColor: ['#F66451', '#5B9BD5', '#D9D9D9'],
      hoverOffset: 4
    }],
    labels: [ln_t('invoice.female'), ln_t('invoice.male'), ln_t('invoice.other')]
  },
  ...defProps({
    usePercentage: true,
    scales: {
      yAxes: [
        {
          ticks: {
            suggestedMin: 0,
            suggestedMax: Math.max(...Object.values(data)?.map(i => i.percentage)) + 5
          }
        }
      ]
    }
  })
})

export const ageChartProps = ({ data }) => {
  const cleanKey = key => key?.split('-')?.[0]?.replace('+', '')
  const sorted = Object.entries(data).sort(([key1], [key2]) => cleanKey(key1) - cleanKey(key2))
  return ({
    data: {
      datasets: [{
        data: sorted?.map(([_, i]) => i.percentage),
        backgroundColor: sorted?.map(([_, i]) => `rgba(${ageRgbNumber}, ${i.percentage > 20 ? 1 : 0.5})`),
        hoverOffset: 4
      }],
      labels: sorted?.map(([key]) => key)
    },
    ...defProps({
      usePercentage: true,
      tooltips: { mode: 'index' }
    })
  })
}

export const trendChartProps = ({ data, ln_t }) => ({
  data: {
    datasets: [
      {
        label: ln_t('invoice.quantity'),
        data: data?.map(i => i.inv_num),
        backgroundColor: '#98CCC4',
        hoverOffset: 4,
        order: 1,
        yAxisID: 'left'
      },
      {
        label: ln_t('invoice.transactions'),
        data: data?.map(i => i.uuid),
        backgroundColor: '#FFDF7D',
        hoverOffset: 4,
        order: 1,
        yAxisID: 'left'
      },
      {
        label: ln_t('invoice.totalConsumers'),
        data: data?.map(i => i.amount),
        borderColor: 'rgba(197,90,17,1)',
        backgroundColor: 'transparent',
        type: 'line',
        order: 0,
        yAxisID: 'right',
        tension: 0
      }
    ],
    labels: data?.map(i => i.date)
  },
  ...defProps({
    scales: {
      yAxes: [
        {
          id: 'left',
          type: 'linear',
          position: 'left',
          ticks: {
            suggestedMin: 0,
            suggestedMax: Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat()) + 10,
            callback: value => value?.toLocaleString()
          }
        },
        {
          id: 'right',
          type: 'linear',
          position: 'right',
          gridLines: { display: false },
          ticks: {
            suggestedMax: Math.max(...data?.map(i => [i.inv_num, i.uuid])?.flat()) + 100,
            callback: value => value?.toLocaleString()
          }
        }
      ]
    },
    height: 100
  })
})

export const channelLabel = data => Object.keys(data?.[0])?.filter(i => i !== 'date')
export const channelChartProps = ({ data }) => ({
  data: {
    datasets: channelLabel(data)?.map((channel, idx) => ({
      label: channel,
      data: data?.map(i => i?.[channel]),
      borderColor: channelColors[idx % channelColors?.length],
      backgroundColor: 'transparent',
      tension: 0
    })),
    labels: data?.map(i => i.date)
  },
  ...defProps({
    scales: {
      yAxes: [{
        ticks: {
          suggestedMax: Math.max(...channelLabel(data)?.map(channel => data?.map(i => i?.[channel]))?.flat()) + 10,
          callback: value => value?.toLocaleString()
        }
      }]
    },
    height: 100,
    legend: {
      display: true,
      position: 'bottom',
      labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 6 }
    }
  })
})

export const channelShareChartProps = ({ data }) => ({
  data: {
    datasets: [{
      data: Object.values(data)?.map(i => i?.percentage),
      backgroundColor: channelShareColors
    }],
    labels: Object.keys(data)
  },
  ...defProps({
    height: 100,
    labelAlignCenter: true,
    usePercentage: true,
    legend: {
      display: true,
      position: 'right',
      labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 6 }
    }
  })
})
